import { useCallback, useMemo, useState } from "react";
import {
  FileEarmarkPdf,
  FileEarmarkWord,
  FileEarmarkImage,
  FileEarmarkText,
  FileEarmarkSpreadsheet,
  FileEarmarkCode,
  FileEarmark,
} from "react-bootstrap-icons";

export const FILE_STATUS = {
  VALID: "valid",
  TOO_LARGE: "error-size",
  OVER_LIMIT: "error-limit",
  BAD_TYPE: "error-type",
};

export const ACCEPT_MIME = {
  "image/*": [],
  "application/pdf": [".pdf"],
  "application/msword": [".doc"],
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [
    ".docx",
  ],
  "text/xml": [".xml"],
  "text/html": [".html", ".htm"],
  "text/plain": [".txt"],
  "application/rtf": [".rtf"],
  "text/csv": [".csv"],
};

/* Same list but by extension – used when the browser
   doesn't give a reliable MIME type (drag-from-ZIP, etc.) */
export const EXT_WHITELIST = [
  "jpg",
  "jpeg",
  "png",
  "gif",
  "bmp",
  "tif",
  "tiff",
  "webp",
  "svg",
  "pdf",
  "doc",
  "docx",
  "xml",
  "html",
  "htm",
  "txt",
  "rtf",
  "csv",
];

/* ──────────────────────────────────────────────────────────────────
   Helpers
   ──────────────────────────────────────────────────────────────── */
const MIME_REGEX = new RegExp(
  [
    "^image/", // any image/*
    "pdf$",
    "msword$",
    "vnd\\.openxmlformats-officedocument\\.wordprocessingml\\.document$",
    "xml$",
    "html$",
    "plain$",
    "rtf$",
    "csv$",
  ].join("|"),
  "i"
);

export const bytesToSize = (bytes = 0) => {
  const units = ["Bytes", "KB", "MB", "GB"];
  if (!bytes) return "0 Bytes";
  const idx = Math.floor(Math.log(bytes) / Math.log(1024));
  const size = bytes / 1024 ** idx;
  return `${size.toLocaleString(undefined, { maximumFractionDigits: 2 })} ${units[idx]}`;
};

/* Checks MIME first (fast path).  
   If empty / generic, falls back to file-extension whitelist. */
export const hasAllowedType = (file) => {
  if (file.type && MIME_REGEX.test(file.type)) return true;
  const ext = file.name.split(".").pop().toLowerCase();
  return EXT_WHITELIST.includes(ext);
};

/* Maps a file to an icon shown in the list – purely cosmetic */
export const pickIcon = (file) => {
  const ext = file.name.split(".").pop().toLowerCase();
  if (
    file.type.startsWith("image/") ||
    ["jpg", "jpeg", "png", "gif", "bmp", "svg", "webp", "tif", "tiff"].includes(
      ext
    )
  )
    return FileEarmarkImage;
  if (ext === "pdf") return FileEarmarkPdf;
  if (ext === "doc" || ext === "docx") return FileEarmarkWord;
  if (ext === "csv") return FileEarmarkSpreadsheet;
  if (["xml", "html", "htm"].includes(ext)) return FileEarmarkCode;
  if (["txt", "rtf"].includes(ext)) return FileEarmarkText;
  return FileEarmark;
};

/* ──────────────────────────────────────────────────────────────────
   useFileManager – owns all state/logic so UI stays dumb
   ──────────────────────────────────────────────────────────────── */
export const useFileManager = ({ defaultFiles = [], maxFiles, maxSize }) => {
  /* helper to attach a UUID + status to a raw File */
  const createItem = useCallback(
    (file, status) => ({ id: crypto.randomUUID(), file, status }),
    []
  );

  /* initial load – walk defaults & tag them appropriately */
  const [items, setItems] = useState(() => {
    let validCount = 0;
    return defaultFiles.map((file) => {
      if (!hasAllowedType(file)) return createItem(file, FILE_STATUS.BAD_TYPE);
      if (file.size > maxSize) return createItem(file, FILE_STATUS.TOO_LARGE);
      if (validCount >= maxFiles)
        return createItem(file, FILE_STATUS.OVER_LIMIT);
      validCount += 1;
      return createItem(file, FILE_STATUS.VALID);
    });
  });

  /* Ensures statuses stay correct when list changes
     (e.g. removing a valid file may promote an overflow one). */
  const reconcile = useCallback(
    (list) => {
      let validCount = 0;
      return list.map((it) => {
        if (
          it.status === FILE_STATUS.TOO_LARGE ||
          it.status === FILE_STATUS.BAD_TYPE
        )
          return it; // immutable errors
        if (validCount < maxFiles) {
          validCount += 1;
          return { ...it, status: FILE_STATUS.VALID };
        }
        return { ...it, status: FILE_STATUS.OVER_LIMIT };
      });
    },
    [maxFiles]
  );

  const validFiles = useMemo(
    () => items.filter((i) => i.status === FILE_STATUS.VALID).map((i) => i.file),
    [items]
  );

  const addFiles = useCallback(
    (incoming) => {
      if (!incoming.length) return;
      setItems((prev) => {
        const merged = [
          ...prev,
          ...incoming.map((f) =>
            createItem(
              f,
              !hasAllowedType(f)
                ? FILE_STATUS.BAD_TYPE
                : f.size > maxSize
                ? FILE_STATUS.TOO_LARGE
                : FILE_STATUS.OVER_LIMIT // provisional; reconcile may promote
            )
          ),
        ];
        return reconcile(merged);
      });
    },
    [createItem, maxSize, reconcile]
  );

  const removeFile = (id) =>
    setItems((prev) => reconcile(prev.filter((it) => it.id !== id)));

  const clearAll = () => setItems([]);

  return { items, validFiles, addFiles, removeFile, clearAll };
};
